import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { RequireAuth, RequireVerified, RequireAdmin } from "@/components/ProtectedRoute";
import Analytics from "@/components/Analytics";

// Public
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Sectors from "@/pages/public/Sectors";
import Process from "@/pages/public/Process";
import Membership from "@/pages/public/Membership";
import CaseStudies from "@/pages/public/CaseStudies";
import CaseStudyDetail from "@/pages/public/CaseStudyDetail";
import Contact from "@/pages/public/Contact";
import HowToUse from "@/pages/public/HowToUse";
import Legal from "@/pages/public/Legal";
import Unsubscribe from "@/pages/public/Unsubscribe";
import ConfirmEmail from "@/pages/public/ConfirmEmail";
import ResetPassword from "@/pages/public/ResetPassword";
import Auth from "@/pages/public/Auth";
import AccessPending from "@/pages/public/AccessPending";
import OrlaGate from "@/pages/OrlaGate";

// Member
import Deals from "@/pages/member/Deals";
import DealDetail from "@/pages/member/DealDetail";
import Account from "@/pages/member/Account";
import Notifications from "@/pages/member/Notifications";

// Admin
import AdminHome from "@/pages/admin/AdminHome";
import SystemHealth from "@/pages/admin/SystemHealth";
import ActivityTrail from "@/pages/admin/ActivityTrail";
import RepoHealth from "@/pages/admin/RepoHealth";
import CrossReview from "@/pages/admin/CrossReview";
import AdminOrla from "@/pages/admin/AdminOrla";
import WebhookSecrets from "@/pages/admin/WebhookSecrets";
import SecretFingerprint from "@/pages/admin/SecretFingerprint";
import AdminQuantus from "@/pages/admin/AdminQuantus";

// Bridge
import BridgeInbox from "@/pages/bridge/BridgeInbox";
import BridgePipeline from "@/pages/bridge/BridgePipeline";
import BridgeSettings from "@/pages/bridge/BridgeSettings";
import BridgeArchive from "@/pages/bridge/BridgeArchive";
import BridgeHowItWorks from "@/pages/bridge/BridgeHowItWorks";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Analytics />
          <Routes>
            {/* Public marketing */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/sectors" element={<Sectors />} />
            <Route path="/process" element={<Process />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/how-to-use" element={<HowToUse />} />
            <Route path="/orla" element={<OrlaGate />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/access-pending" element={<AccessPending />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/legal/:type" element={<Legal />} />

            {/* Authenticated member */}
            <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
            <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />

            {/* Verified investor area */}
            <Route path="/deals" element={<RequireVerified><Deals /></RequireVerified>} />
            <Route path="/deals/:id" element={<RequireVerified><DealDetail /></RequireVerified>} />

            {/* Admin console */}
            <Route path="/admin" element={<RequireAdmin><AdminHome /></RequireAdmin>} />
            <Route path="/admin/system-health" element={<RequireAdmin><SystemHealth /></RequireAdmin>} />
            <Route path="/admin/activity-trail" element={<RequireAdmin><ActivityTrail /></RequireAdmin>} />
            <Route path="/admin/repo-health" element={<RequireAdmin><RepoHealth /></RequireAdmin>} />
            <Route path="/admin/cross-review" element={<RequireAdmin><CrossReview /></RequireAdmin>} />
            <Route path="/admin/orla" element={<RequireAdmin><AdminOrla /></RequireAdmin>} />
            <Route path="/admin/webhook-secrets" element={<RequireAdmin><WebhookSecrets /></RequireAdmin>} />
            <Route path="/admin/secret-fingerprint" element={<RequireAdmin><SecretFingerprint /></RequireAdmin>} />
            <Route path="/admin/quantus" element={<RequireAdmin><AdminQuantus /></RequireAdmin>} />

            {/* Deal Bridge */}
            <Route path="/bridge" element={<RequireAdmin><BridgeInbox /></RequireAdmin>} />
            <Route path="/bridge/pipeline" element={<RequireAdmin><BridgePipeline /></RequireAdmin>} />
            <Route path="/bridge/settings" element={<RequireAdmin><BridgeSettings /></RequireAdmin>} />
            <Route path="/bridge/archive" element={<RequireAdmin><BridgeArchive /></RequireAdmin>} />
            <Route path="/bridge/how-it-works" element={<RequireAdmin><BridgeHowItWorks /></RequireAdmin>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
